import { AfterViewInit, ChangeDetectionStrategy, Component, DestroyRef, effect, input, OnInit, Signal, viewChild } from '@angular/core';
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { NODE_EDIT_TAB_COMPONENT, NodeEditTab, SelectedNodeForEdit } from 'src/app/shared/flow-chart/flow-chart.model';
import { HttpRequestNodeData } from '../../../node-edit.type';
import { Store } from '@ngrx/store';
import { UiV2SelectorComponent } from '@ui-kit/ui-v2-selector/ui-v2-selector.component';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { routerSegments } from '@consts/routes';
import { HttpRequestModel } from '@models/http-request.model';
import { CreateHttpRequestModalComponent } from '@modals/create-http-request-modal/create-http-request-modal.component';
import { MatTooltip } from '@angular/material/tooltip';
import { UiCopyClipboardComponent } from '@ui';
import { WorkflowModel } from '@models/workflow.model';
import { WorkflowsSelectors } from '@states/workflow/workflow.selector-types';
import { Observable, take } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpRequestSelectors } from '@states/http-request/http-request.selector-types';
import { HttpRequestActions } from '@states/http-request/http-request.action-types';
import { ConfirmDialogService } from 'src/app/shared/confirm-dialog/confirm-dialog.service';

@Component({
  selector: 'edit-http-request',
  imports: [
    ReactiveFormsModule,
    TranslatePipe,
    UiV2SelectorComponent,
    UiCopyClipboardComponent,
    MatTooltip,
  ],
  templateUrl: './edit-http-request.component.html',
  styleUrl: './edit-http-request.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NODE_EDIT_TAB_COMPONENT,
      useExisting: EditHttpRequestComponent,
    },
  ],
})
export class EditHttpRequestComponent implements OnInit, NodeEditTab, AfterViewInit {
  public selectedNodeForEdit = input<SelectedNodeForEdit<HttpRequestNodeData>>();
  public markSelectAsTouched = input<boolean>(false);
  public disableForm = input<boolean>(false);

  public form!: UntypedFormGroup;

  public selectHttpRequestsSig: Signal<HttpRequestModel.DocumentMongo[]> =
    this.store$.selectSignal(HttpRequestSelectors.selectHttpRequests);
  public selectIsInitialLoadedSig: Signal<boolean> =
    this.store$.selectSignal(HttpRequestSelectors.selectIsInitialLoaded);
  public selectNodeSig: Signal<WorkflowModel.NodeConfigDocument> =
    this.store$.selectSignal(WorkflowsSelectors.selectNode);

  public selectHttpRequests$: Observable<HttpRequestModel.DocumentMongo[]> =
    this.store$.select(HttpRequestSelectors.selectHttpRequests);

  public selectSig = viewChild(UiV2SelectorComponent);

  public httpRequest: HttpRequestModel.DocumentMongo;

  constructor(
    private fb: UntypedFormBuilder,
    private translateService: TranslateService,
    private store$: Store,
    private router: Router,
    private dialog: MatDialog,
    private destroyRef: DestroyRef,
    private confirmDialogService: ConfirmDialogService,
  ) {
    effect(() => {
      const isInitialLoaded = this.selectIsInitialLoadedSig();

      if (!isInitialLoaded) {
        this.selectSig()?.selectedItem?.disable();
      } else if (!this.disableForm()) {
        this.selectSig()?.selectedItem?.enable();
      }
    })
  }

  public ngOnInit(): void {
    const httpReqs = this.selectHttpRequestsSig();

    if (this.markSelectAsTouched()) {
      this.selectSig()?.selectedItem?.markAsTouched();
    }

    if (!httpReqs?.length) {
      this.store$.dispatch(HttpRequestActions.getOrganizationHttpRequests());
    }

    this.form = this.fb.group({
      name: [
        {
          value: this.selectedNodeForEdit()?.data?.name ??
            this.translateService.instant('FLOW_CHART.NODES.HTTPREQ.NAME'),
          disabled: this.disableForm(),
        },
        [Validators.required],
      ],
      httpRequestId: [this.selectedNodeForEdit()?.data?.httpRequestId ?? '',
      [Validators.required]],
    });

    this.selectHttpRequests$.pipe(
      takeUntilDestroyed(this.destroyRef)
    )
      .subscribe((httpRequests) => {
        if (httpRequests?.length && this.selectNodeSig()?.preview) {
          const id = this.selectNodeSig().preview.options['httpRequestId'];

          this.httpRequest = httpRequests.find((httpRequest) => httpRequest._id === id);
        }
      })
  }

  public create(): void {
    this.dialog
      .open(CreateHttpRequestModalComponent, {
        panelClass: 'modal-no-padding',
        width: '760px',
        maxHeight: '80vh',
        data: null,
      })
      .afterClosed()
      .subscribe((response: { type: string; httpRequest: HttpRequestModel.DocumentMongo }) => {
        if (response?.httpRequest) {
          this.store$.dispatch(HttpRequestActions.getOrganizationHttpRequests());

          this.form.controls['httpRequestId'].setValue(response.httpRequest._id);
          this.selectSig().matSelect()?.close();
        }
      });
  }

  public manage(): void {
    this.confirmDialogService
      .discard()
      .pipe(take(1))
      .subscribe((leave) => {
        if (leave) {
          this.router.navigate([routerSegments.settings, routerSegments.integrations, routerSegments.httpRequests]);
        }
      });
  }

  public edit(): void {
    this.dialog
      .open(CreateHttpRequestModalComponent, {
        panelClass: 'modal-no-padding',
        width: '760px',
        maxHeight: '80vh',
        data: {
          httpRequest: this.httpRequest,
        },
      })
  }

  public ngAfterViewInit(): void {
    if (this.disableForm()) {
      this.selectSig().selectedItem?.disable();
    }
  }
}
